<template>
  <div class="h3 mb-4">Сопоставление категории номеров</div>
  <div class="paragraph-s_h mb-10 text-secondary">Добавьте категорию номера, если её нет в списке ваших номеров</div>
  <e-form v-model="isValid" ref="formRef" data-wide-content>
    <e-alert
      v-if="integrationError === ChannelManagerCustomError.ChannelManagerAdditionalMappingRequiredError"
      variant="error"
      class="mb-10"
    >
      <div class="paragraph-m_h font-600">
        В настройки подключения на стороне Менеджера Каналов были внесены изменения
      </div>
      <div class="paragraph-s_h text-secondary mt-1">Необходимо настроить дополнительные сопоставления</div>
    </e-alert>
    <e-data-table content-wrap :headers="headers" :items="items">
      <template v-slot:[`item.categoryExtranetId`]="{ item }">
        <div class="d-flex align-center">
          <e-select
            v-model="item.categoryExtranetId"
            :items="categories"
            :error-messages="duplicated(item.categoryExtranetId, item.externalId)"
            :rules="[required()]"
            placeholder="Выберите категорию номера"
            item-title="name[0].value"
            item-value="cid"
            @update:modelValue="saveMapping"
          >
            <template #prepend-item>
              <div class="pa-2">
                <e-btn prepend-icon-src="plus" variant="link" size="M" @click="showRoomCategoryDialog(item.externalId)">
                  Добавить категорию номера
                </e-btn>
              </div>
            </template>
          </e-select>
          <e-btn
            :disabled="!item.categoryExtranetId"
            icon
            size="S"
            variant="ghost"
            class="ml-1 text-link"
            @click="editRoomCategory(item.categoryExtranetId)"
          >
            <e-icon name="documentText" />
          </e-btn>
        </div>
      </template>
    </e-data-table>

    <teleport v-if="isMounted" defer :to="`#${travelLineIntegrationSubmitButtonTeleport}`">
      <e-btn 
        append-icon-src="arrowRight" 
        :loading="combinedLoading" 
        variant="primary" 
        @click="submit"
      > 
        Далее 
      </e-btn>
    </teleport>
  </e-form>
  <room-category-dialog
    v-model="isRoomCategoryDialogShown"
    edit-immediate
    :room-category="editingRoomCategory"
    @success:add="onAddRoomCategory"
    @success:update="onUpdateRoomCategory"
    @after-leave="onDialogClose"
  />
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core';
import { computed, onMounted, ref, toRef } from 'vue';
import type { DataTableHeaders } from '@/core/types/components/table';
import type { RoomCategoriesItemType } from '@/parts/room-categories/store/roomCategories';
import { EAlert, EBtn, EDataTable, EForm, EIcon, ESelect } from '@/core/components/ui';
import { travelLineIntegrationSubmitButtonTeleport } from '@/core/consts/teleport';
import { required } from '@/core/utils/validation';
import { ChannelManagerCode } from '@/graphql/generated';
import RoomCategoryDialog from '@/parts/room-categories/components/RoomCategoryDialog.vue';
import { useTravelLineStore } from '@/parts/tariffs/store/travelLine';
import { ChannelManagerCustomError } from '@/parts/tariffs/types/travel-line';

const emit = defineEmits<{
  (event: 'next'): void;
}>();

const { channelManagerCode } = useTravelLineStore();

const headers: DataTableHeaders = [
  {
    key: 'categoryTL',
    title:
      channelManagerCode.value === ChannelManagerCode.TravelLine ? 'Категория номера TL' : 'Категория номера Exely',
  },
  {
    key: 'categoryExtranetId',
    title: 'Категория Номера Экстранет',
    sortable: false,
  },
];

const isMounted = useMounted();

const { integration, saveRoomCategoriesMapping, updateRoomCategories, integrationError } = useTravelLineStore();

const isSavingMapping = ref(false);

const onUpdateRoomCategory = (data: RoomCategoriesItemType) => {
  const roomMappingMaster = integration.value?.master?.steps.roomCategoriesMapping;

  if (!roomMappingMaster) {
    return;
  }

  const existingRoomCategoryIndex = roomMappingMaster.roomCategories.findIndex(
    (roomCategory) => roomCategory.cid === data.cid,
  );

  if (existingRoomCategoryIndex !== -1) {
    roomMappingMaster.roomCategories.splice(existingRoomCategoryIndex, 1, data);
  }
};

const items = ref<
  {
    categoryTL: string;
    externalId: string;
    categoryExtranetId: string | null;
  }[]
>([]);

const saveMapping = async () => {
  isSavingMapping.value = true;
  
  try {
    await saveRoomCategoriesMapping(
      items.value.map((item) => ({
        externalID: item.externalId,
        cid: item.categoryExtranetId || '',
      })),
    );
  } finally {
    isSavingMapping.value = false;
  }
};

const roomCategoriesMapping = toRef(() => integration.value?.master?.steps.roomCategoriesMapping);

const categories = computed(() => {
  if (!roomCategoriesMapping.value) {
    return [];
  }

  return roomCategoriesMapping.value.roomCategories;
});

const setItems = () => {
  if (!roomCategoriesMapping.value) {
    return;
  }

  items.value = roomCategoriesMapping.value.translationTable.map((item) => ({
    categoryTL: item.externalObject.name,
    externalId: item.externalObject.id,
    categoryExtranetId: item.cid || null,
  }));
};

onMounted(setItems);

const isRoomCategoryDialogShown = ref(false);
let TLRoomCategoryToMapAfterCreate: string | null = null;

const showRoomCategoryDialog = (externalId?: string) => {
  if (externalId) {
    TLRoomCategoryToMapAfterCreate = externalId;
  }

  isRoomCategoryDialogShown.value = true;
};

const onAddRoomCategory = (data: RoomCategoriesItemType) => {
  const roomMappingMaster = integration.value?.master?.steps.roomCategoriesMapping;

  if (!roomMappingMaster) {
    return;
  }

  roomMappingMaster.roomCategories.push(data);

  if (TLRoomCategoryToMapAfterCreate) {
    const TLRoomCategory = items.value?.find((i) => i.externalId === TLRoomCategoryToMapAfterCreate);

    if (TLRoomCategory) {
      TLRoomCategory.categoryExtranetId = data.cid;
      saveMapping();
    }

    TLRoomCategoryToMapAfterCreate = null;
  }
};

const editingRoomCategory = ref<RoomCategoriesItemType>();

const editRoomCategory = (roomCategoryId: string | null) => {
  editingRoomCategory.value = categories.value.find((c) => c.cid === roomCategoryId);
  showRoomCategoryDialog();
};

const duplicated = (cid: string | null | undefined, externalId: string) => {
  if (!cid) {
    return '';
  }

  return items.value.find((item) => item.categoryExtranetId === cid && item.externalId !== externalId)
    ? 'Категория уже сопоставлена'
    : '';
};

const onDialogClose = () => {
  editingRoomCategory.value = undefined;
};

const isLoading = ref(false);
const isValid = ref(false);
const formRef = ref();

const combinedLoading = computed(() => isSavingMapping.value || isLoading.value);

const submit = async () => {
  formRef.value?.validate();

  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  const result = await updateRoomCategories(
    items.value
      .filter((item) => item.categoryExtranetId)
      .map((item) => ({
        externalID: item.externalId,
        cid: item.categoryExtranetId!,
      })),
  );

  isLoading.value = false;

  if (!result?.errors) {
    integrationError.value = undefined;
    emit('next');
  } else {
    setItems();
  }
};
</script>

<style scoped lang="scss"></style>
